import React, { useEffect, useState } from "react";
import Link from "next/link";
import Icon from "../components/Icon";
import ApiService from "../../../services/ApiService";
import { applyLang } from "../../../i18n";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { sharedStyles, SectionHead } from "./_shared";
import { resolveImage } from "../utils";

const styles = `
.t26-gallery__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: clamp(14px, 2vw, 20px);
}
.t26-gallery__cell {
  position: relative;
  aspect-ratio: 4 / 5;
  border-radius: var(--r-lg);
  overflow: hidden;
  isolation: isolate;
  background: color-mix(in oklab, var(--primary) 20%, var(--bg-2));
  cursor: pointer;
  display: block;
}
.t26-gallery__cell img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform .5s ease;
}
.t26-gallery__cell:hover img { transform: scale(1.06); }
.t26-gallery__cell::after {
  content: "";
  position: absolute; inset: 0;
  background: linear-gradient(180deg, transparent 50%, rgba(8,14,22,.75) 100%);
  z-index: 1;
}
.t26-gallery__caption {
  position: absolute; z-index: 2;
  inset: auto 0 0 0;
  padding: 16px 18px;
  color: white;
  display: flex; flex-direction: column; gap: 4px;
}
.t26-gallery__caption-title {
  font-family: var(--font-display);
  font-size: 17px; font-weight: 700;
  line-height: 1.2;
  text-shadow: 0 1px 4px rgba(0,0,0,.4);
}
.t26-gallery__caption-sub {
  font-size: 11px; font-weight: 600;
  letter-spacing: .12em; text-transform: uppercase;
  opacity: .85;
}
`;

/**
 * Aperçu des galeries photo sur la home.
 * Source : POST /galeries/{orgId} avec {page:1}.
 * Champs utilisés : id, name, bgImage, datetime.
 * URL détail : /galerie/{id} (singulier — convention Ummah/API).
 */
export default function GalleryPreview({ context, limit = 4, eyebrow, title, subtitle }) {
    const { org, lang, langValue } = context;
    const [data, setData] = useState([]);
    const [busy, setBusy] = useState(true);

    useEffect(() => {
        if (!org?.id) { setBusy(false); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await ApiService.post("/galeries/" + org.id, { page: 1 });
                if (!cancelled && Array.isArray(res?.data)) {
                    setData(res.data.slice(0, limit));
                }
            } catch (err) {
                // silencieux : preview optionnelle
            } finally {
                if (!cancelled) setBusy(false);
            }
        })();
        return () => { cancelled = true; };
    }, [org?.id, limit]);

    if (busy || data.length === 0) return null;

    const galeries = data.map(g => applyLang(g, lang));

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: sharedStyles + styles }} />
            <section className="t26-section">
                <SectionHead
                    eyebrow={eyebrow || t`Galerie`}
                    title={title || t`Souvenirs en images`}
                    subtitle={subtitle || t`Revivez les moments forts de notre communauté`}
                />

                <div className="t26-gallery__grid">
                    {galeries.map(g => {
                        const cover = g.bgImage || g.image || null;
                        const url = `${org.BASE_WEB_URL}/galerie/${g.id || g._id}`;
                        const dateLabel = g.datetime
                            ? new Date(g.datetime).toLocaleDateString(langValue || "fr", { month: "short", year: "numeric" })
                            : null;
                        return (
                            <Link key={g.id || g._id} href={url} legacyBehavior>
                                <a className="t26-gallery__cell">
                                    {cover && <img src={resolveImage(cover)} alt={g.name || ""} loading="lazy" />}
                                    <div className="t26-gallery__caption">
                                        <span className="t26-gallery__caption-title">{g.name}</span>
                                        {dateLabel && <span className="t26-gallery__caption-sub">{dateLabel}</span>}
                                    </div>
                                </a>
                            </Link>
                        );
                    })}
                </div>

                <div style={{ textAlign: "center" }}>
                    <Link href={`${org.BASE_WEB_URL}/galeries`} legacyBehavior>
                        <a className="t26-section__more">
                            <Trans>Toutes les galeries</Trans>
                            <span className="t26-section__more-arrow">
                                <Icon name="arrowRight" size={14} className="t26-flip-rtl" />
                            </span>
                        </a>
                    </Link>
                </div>
            </section>
        </>
    );
}
